import type { CancellationPolicy } from '../api/hotels.types';

/**
 * Same rule the backend enforces on POST /bookings (hotel-booking-api
 * `features/bookings/cancellation-policy.ts`), applied before the user confirms so a rate the
 * backend would reject goes to the agency inquiry instead of failing:
 *  - no policy, empty, or no readable `dateFrom` → `unavailable` (inquiry);
 *  - earliest `dateFrom` on or before today + N days → `inquiry`;
 *  - otherwise → `bookable` (free cancellation until then).
 * N comes from RAZZLE_HOTEL_CANCELLATION_INQUIRY_DAYS (default 3) and must match the backend's
 * CANCELLATION_POLICY_INQUIRY_DAYS.
 */
export type CancellationDisposition = 'bookable' | 'inquiry' | 'unavailable';

const DEFAULT_INQUIRY_DAYS = 3;

export function getCancellationInquiryDays(): number {
  const raw = Number(process.env.RAZZLE_HOTEL_CANCELLATION_INQUIRY_DAYS);
  return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_INQUIRY_DAYS;
}

/** Parses `2026-09-13 15:47:06` or `2026-09-13` as a UTC calendar date; null if unreadable. */
export function parseCancellationDateFrom(value: string | null | undefined): Date | null {
  const dateOnly = typeof value === 'string' ? value.trim().slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;

  const [year, month, day] = dateOnly.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  const matches =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day;
  return matches ? parsed : null;
}

export function evaluateCancellationPolicy(
  policies: CancellationPolicy[] | null | undefined,
  options: { now?: Date; inquiryDays?: number } = {},
): { disposition: CancellationDisposition; earliestDateFrom: Date | null } {
  const dates = (policies ?? [])
    .map((policy) => parseCancellationDateFrom(policy.dateFrom))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  if (dates.length === 0) return { disposition: 'unavailable', earliestDateFrom: null };

  const now = options.now ?? new Date();
  const deadline = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  deadline.setUTCDate(
    deadline.getUTCDate() + (options.inquiryDays ?? getCancellationInquiryDays()),
  );

  const earliestDateFrom = dates[0];
  return {
    disposition: earliestDateFrom.getTime() <= deadline.getTime() ? 'inquiry' : 'bookable',
    earliestDateFrom,
  };
}
